import { useState, useEffect, useCallback, useMemo } from 'react'
import { Search, ChevronRight, ChevronDown, X, AlertCircle } from 'lucide-react'
import { useApi } from '../../hooks/useApi'
import {
  buildCPVTree,
  filterCPVCodes,
  toggleNodeExpansion,
  getAllSelectedCodes,
  isCodeSelected,
} from '../../utils/cpvHierarchy'

interface CPVCode {
  code: string
  description_pl: string
  description_en: string
  parent_code: string | null
  level: number
}

interface CPVTreeNode extends CPVCode {
  children: CPVTreeNode[]
  expanded: boolean
  visible: boolean
}

interface TreeNodeProps {
  node: CPVTreeNode
  level: number
  selectedCodes: string[]
  onToggleExpand: (code: string) => void
  onSelectionChange: (code: string, selected: boolean) => void
  maxSelection?: number
}

function TreeNode({
  node,
  level,
  selectedCodes,
  onToggleExpand,
  onSelectionChange,
  maxSelection = 100,
}: TreeNodeProps) {
  const isSelected = selectedCodes.includes(node.code)
  const isPartiallySelected = node.children?.some(
    (child) =>
      selectedCodes.includes(child.code) ||
      child.children?.some((grandChild) => selectedCodes.includes(grandChild.code)),
  )
  const hasChildren = node.children && node.children.length > 0
  const canExpand = hasChildren || node.children === undefined // Lazy loading indicator

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation()
    onToggleExpand(node.code)
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation()

    const currentSelectedCount = selectedCodes.length
    const allSelectedCodes = getAllSelectedCodes([node], selectedCodes)
    const wouldAddCount = allSelectedCodes.length - currentSelectedCount

    // Check selection limit
    if (!isSelected && currentSelectedCount + wouldAddCount > maxSelection) {
      return // Don't allow selection that would exceed limit
    }

    onSelectionChange(node.code, !isSelected)
  }

  const handleDivClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    // Trigger the same selection logic as checkbox
    const currentSelectedCount = selectedCodes.length
    const allSelectedCodes = getAllSelectedCodes([node], selectedCodes)
    const wouldAddCount = allSelectedCodes.length - currentSelectedCount

    if (!isSelected && currentSelectedCount + wouldAddCount > maxSelection) {
      return
    }

    onSelectionChange(node.code, !isSelected)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleDivClick(e as any)
    }
  }

  return (
    <div>
      <button
        type="button"
        className={`flex items-center py-2 px-2 hover:bg-surface-hover cursor-pointer group w-full text-left ${
          level > 1 ? 'ml-6' : ''
        }`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={handleDivClick}
        onKeyDown={handleKeyDown}
      >
        {/* Expand/collapse button */}
        <button
          onClick={handleToggleExpand}
          className={`w-4 h-4 mr-2 flex items-center justify-center text-ink-faint hover:text-ink-secondary ${
            !canExpand ? 'invisible' : ''
          }`}
          disabled={!canExpand}
        >
          {node.expanded ? (
            <ChevronDown className="w-3 h-3" />
          ) : (
            <ChevronRight className="w-3 h-3" />
          )}
        </button>

        {/* Checkbox */}
        <input
          type="checkbox"
          checked={isSelected}
          ref={(el) => {
            if (el) el.indeterminate = !isSelected && isPartiallySelected
          }}
          onChange={handleCheckboxChange}
          className="mr-3 rounded border-border text-brand focus:ring-brand"
        />

        {/* Code and description */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center">
            <span className="font-mono text-sm font-medium text-ink mr-2">{node.code}</span>
            <span className="text-sm text-ink-secondary truncate">{node.description_pl}</span>
          </div>
          {node.description_en && node.description_en !== node.description_pl && (
            <div className="text-xs text-ink-muted truncate">{node.description_en}</div>
          )}
        </div>
      </button>

      {/* Children */}
      {node.expanded && node.children && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={child.code}
              node={child}
              level={level + 1}
              selectedCodes={selectedCodes}
              onToggleExpand={onToggleExpand}
              onSelectionChange={onSelectionChange}
              maxSelection={maxSelection}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface CPVBrowserProps {
  onSelectionChange: (codes: string[]) => void
  selectedCodes?: string[]
  maxSelection?: number
}

export default function CPVBrowser({
  onSelectionChange,
  selectedCodes = [],
  maxSelection = 100,
}: CPVBrowserProps) {
  const { getCPVHierarchy, searchCPVCodes, loading, error } = useApi()

  const [treeData, setTreeData] = useState<CPVTreeNode[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [searchResults, setSearchResults] = useState<CPVCode[]>([])
  const [allCPVCodes, setAllCPVCodes] = useState<CPVCode[]>([])

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTerm])

  // Load initial hierarchy
  useEffect(() => {
    const loadHierarchy = async () => {
      try {
        const data = await getCPVHierarchy()
        const tree = buildCPVTree(data.children || [])
        setTreeData(tree)
        setAllCPVCodes(data.children || [])
      } catch (err) {
        console.error('Failed to load CPV hierarchy:', err)
      }
    }

    loadHierarchy()
  }, [getCPVHierarchy])

  // Handle search
  useEffect(() => {
    const performSearch = async () => {
      if (!debouncedSearchTerm.trim()) {
        setIsSearching(false)
        setSearchResults([])
        return
      }

      setIsSearching(true)
      try {
        const results = await searchCPVCodes(debouncedSearchTerm, 50)
        setSearchResults(results.results || [])
      } catch (err) {
        console.error('Search failed:', err)
        setSearchResults([])
      } finally {
        setIsSearching(false)
      }
    }

    performSearch()
  }, [debouncedSearchTerm, searchCPVCodes])

  // Toggle node expansion
  const handleToggleExpand = useCallback((code: string) => {
    setTreeData((prevTree) => toggleNodeExpansion(prevTree, code))
  }, [])

  // Handle selection change with parent/child propagation
  const handleSelectionChange = useCallback(
    (code: string, selected: boolean) => {
      const newSelectedCodes = selected
        ? [...selectedCodes, code]
        : selectedCodes.filter((c) => c !== code)

      onSelectionChange(newSelectedCodes)
    },
    [selectedCodes, onSelectionChange],
  )

  // Remove selected code
  const handleRemoveCode = useCallback(
    (code: string) => {
      const newSelectedCodes = selectedCodes.filter((c) => c !== code)
      onSelectionChange(newSelectedCodes)
    },
    [selectedCodes, onSelectionChange],
  )

  // Get display data (filtered tree or search results)
  const displayData = useMemo(() => {
    if (debouncedSearchTerm.trim()) {
      // Convert search results to tree format for display
      return searchResults.map((result) => ({
        ...result,
        children: [],
        expanded: false,
        visible: true,
      }))
    } else {
      // Filter tree if needed
      return filterCPVCodes(treeData, '', selectedCodes)
    }
  }, [debouncedSearchTerm, searchResults, treeData, selectedCodes])

  const selectedCount = selectedCodes.length
  const isAtLimit = selectedCount >= maxSelection

  return (
    <div className="bg-surface-raised border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-ink">Przeglądarka kodów CPV</h3>
        <div className="flex items-center space-x-2">
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              isAtLimit
                ? 'bg-danger/10 text-danger'
                : selectedCount > 0
                  ? 'bg-brand-bg text-brand'
                  : 'bg-surface-inset text-ink'
            }`}
          >
            {selectedCount}/{maxSelection}
          </span>
        </div>
      </div>

      <div className="space-y-4">
        {/* Search input */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-ink-faint" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-border rounded-md leading-5 bg-surface-raised placeholder-ink-muted focus:outline-none focus:placeholder-ink-muted focus:ring-1 focus:ring-brand focus:border-brand"
            placeholder="Wyszukaj kody CPV..."
          />
          {isSearching && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand"></div>
            </div>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-danger/5 border border-danger/20 rounded-lg p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-danger" />
              <div className="ml-3">
                <p className="text-sm text-danger">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tree view */}
        <div className="border border-border rounded-lg max-h-96 overflow-y-auto">
          {loading && !treeData.length ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
              <p className="text-ink-secondary">Ładowanie hierarchii CPV...</p>
            </div>
          ) : displayData.length === 0 ? (
            <div className="p-8 text-center text-ink-muted">
              {debouncedSearchTerm.trim()
                ? `Brak wyników dla "${debouncedSearchTerm}"`
                : 'Brak danych CPV'}
            </div>
          ) : (
            <div className="divide-y divide-border-subtle">
              {displayData.map((node) => (
                <TreeNode
                  key={node.code}
                  node={node}
                  level={1}
                  selectedCodes={selectedCodes}
                  onToggleExpand={handleToggleExpand}
                  onSelectionChange={handleSelectionChange}
                  maxSelection={maxSelection}
                />
              ))}
            </div>
          )}
        </div>

        {/* Selected codes display */}
        {selectedCodes.length > 0 && (
          <div className="bg-brand-bg border border-brand rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-brand">
                Wybrane kody CPV ({selectedCount})
              </p>
              {isAtLimit && (
                <span className="text-xs text-danger font-medium">Osiągnięto limit wyboru</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedCodes.map((code) => {
                // Find code info
                const codeInfo = allCPVCodes.find((c) => c.code === code)
                return (
                  <span
                    key={code}
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-brand-bg text-brand"
                  >
                    <span className="font-mono mr-1">{code}</span>
                    {codeInfo && (
                      <span className="truncate max-w-32 mr-1">{codeInfo.description_pl}</span>
                    )}
                    <button
                      onClick={() => handleRemoveCode(code)}
                      className="ml-1 hover:bg-brand-bg rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
